/**
 * Les champs d'une fiche d'employé, groupés pour la saisie — chunk 9.BK.1.
 *
 * ## Pourquoi trois groupes et pas vingt-deux champs à la file
 *
 * Le classeur GFS porte 22 colonnes ; six seulement sont indispensables pour
 * tarifer, plus la rémunération. Les afficher toutes au même niveau ferait
 * abandonner la moitié des gens dès le deuxième employé.
 *
 * L'ordre est donc : ce qu'il faut, la paie, puis le reste — replié. Le repli
 * n'est pas un rangement esthétique : c'est ce qui rend une saisie de 22
 * colonnes praticable sur un téléphone.
 *
 * **Aucune règle de validation ici.** Le fait qu'un champ soit dans le groupe
 * « requis » n'en fait pas une garde côté navigateur : c'est le serveur qui
 * refuse, et la liste des manques vient de lui.
 */

export type TypeChamp = "text" | "date" | "number" | "email" | "tel";

export type Champ = { readonly cle: string; readonly type: TypeChamp };

/** Ce sans quoi un employé ne peut pas être tarifé. */
export const CHAMPS_REQUIS: readonly Champ[] = [
  { cle: "employee_number", type: "text" },
  { cle: "first_name", type: "text" },
  { cle: "last_name", type: "text" },
  { cle: "birth_date", type: "date" },
  { cle: "hire_date", type: "date" },
  { cle: "coverage", type: "text" },
];

/** Un salaire annuel, ou un taux horaire avec les heures. Le serveur tranche. */
export const CHAMPS_PAIE: readonly Champ[] = [
  { cle: "annual_salary", type: "number" },
  { cle: "hourly_rate", type: "number" },
  { cle: "hours_per_week", type: "number" },
];

/** Utiles, mais non applicables à tout le monde. */
export const CHAMPS_FACULTATIFS: readonly Champ[] = [
  { cle: "gender", type: "text" },
  { cle: "smoker", type: "text" },
  { cle: "employment_status", type: "text" },
  { cle: "employment_type", type: "text" },
  { cle: "mobile_phone", type: "tel" },
  { cle: "email", type: "email" },
  { cle: "additional_email", type: "email" },
  { cle: "additional_phone", type: "tel" },
  { cle: "division", type: "text" },
  { cle: "occupation", type: "text" },
  { cle: "coverage_start", type: "date" },
  { cle: "coverage_end", type: "date" },
  { cle: "note", type: "text" },
];

export const CHAMPS_PARTICIPANT: readonly Champ[] = [
  ...CHAMPS_REQUIS,
  ...CHAMPS_PAIE,
  ...CHAMPS_FACULTATIFS,
];

export const CHAMPS_DEPENDANT: readonly Champ[] = [
  { cle: "first_name", type: "text" },
  { cle: "last_name", type: "text" },
  { cle: "relationship", type: "text" },
  { cle: "birth_date", type: "date" },
  { cle: "gender", type: "text" },
  { cle: "smoker", type: "text" },
  { cle: "coverage_start", type: "date" },
  { cle: "coverage_end", type: "date" },
  { cle: "dependent_status", type: "text" },
];

/**
 * Prépare une ligne pour le fil.
 *
 * Les champs numériques partent en nombre ou en `null` — jamais en chaîne vide,
 * que le contrat refuserait. Les autres partent **tels que saisis** : aucune
 * mise en forme, aucun `trim`. Ce que la personne a tapé est la donnée.
 */
export function versLeFil(
  valeurs: Record<string, string>,
  champs: readonly Champ[],
): Record<string, unknown> {
  const sortie: Record<string, unknown> = {};
  for (const { cle, type } of champs) {
    const brut = valeurs[cle];
    if (brut === undefined || brut === "") {
      sortie[cle] = null;
      continue;
    }
    if (type === "number") {
      const n = Number(brut.replace(",", "."));
      sortie[cle] = Number.isFinite(n) ? n : null;
      continue;
    }
    sortie[cle] = brut;
  }
  return sortie;
}
